import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Settings,
  Lock,
  CheckCircle,
  Bell,
  Clock,
  LogOut,
  Shield,
  Zap,
  Heart,
  ArrowLeft,
} from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
// Assuming db is correctly exported from "../firebase1"
import { db } from "../firebase1"; 

// --- FRAMER MOTION VARIANTS ---
const formVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 100, damping: 15, delay: 0.1 },
  },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100, damping: 15, duration: 0.6 },
  },
};

// --- TOGGLE SWITCH COMPONENT (DISABLED FOR DEMO) ---
// This remains the same as it is a helper component.
const ToggleSwitch = ({ label, isChecked, isDisabled }) => (
  <div
    className={`flex items-center justify-between py-4 px-5 border-b last:border-b-0 transition-opacity ${
      isDisabled ? "opacity-50" : "opacity-100 border-gray-100"
    }`}
  >
    <span className="text-gray-700 font-medium flex items-center gap-3 text-base">
      {label.includes("Restrict Content") && (
        <Lock className="w-5 h-5 text-red-500" />
      )}
      {label.includes("Enable Notifications") && (
        <Bell className="w-5 h-5 text-purple-500" />
      )}
      {label.includes("Daily Time Limit") && (
        <Clock className="w-5 h-5 text-pink-500" />
      )}
      {label}
    </span>
    <button
      // onClick is disabled in this simulation
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-default ${
        isChecked ? "bg-purple-600" : "bg-gray-300"
      }`}
      aria-checked={isChecked}
      disabled={isDisabled}
      role="switch"
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-md ${
          isChecked ? "translate-x-[22px]" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);

// --- EMAIL NOTIFY FORM COMPONENT (Moved out of ParentControlPage) ---
// Note: This component uses Firebase/Firestore, which should be configured in ../firebase1
 function EmailNotifyForm() {
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, "parentControlWaitlist"), {
        email,
        createdAt: serverTimestamp(),
        source: "parent-control-page",
      });

      setSuccess(true);
      setEmail("");

      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError("Something went wrong. Try again.");
      console.error(err);
    }

    setLoading(false);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* SUCCESS BOX */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mb-4 p-4 bg-green-600 text-white font-semibold rounded-xl shadow-md text-center"
        >
          🎉 Thank you! You’ll be notified when it's ready.
        </motion.div>
      )}

      {/* ERROR BOX */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-4 p-4 bg-red-500 text-white font-semibold rounded-xl shadow-md text-center"
        >
          {error}
        </motion.div>
      )}

      {/* FORM */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <input
          type="email"
          className="px-4 py-3 rounded-xl text-black w-full focus:ring-2 focus:ring-white outline-none"
          placeholder="Enter your email..."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <button
          type="submit"
          disabled={loading}
          className="bg-white text-purple-700 font-bold py-3 rounded-xl shadow-lg hover:bg-gray-100 transition"
        >
          {loading ? "Submitting..." : "Notify Me"}
        </button>
      </form>
    </div>
  );
}

// --- MAIN PARENT CONTROL PAGE COMPONENT ---
export default function ParentControlPage() {
  // State is maintained to power the visual simulation
  const [childUser] = useState({
    name: "Max",
    email: "max@abc.com",
  });
  const [controls] = useState({
    restrictContent: true,
    enableNotifications: false,
    dailyTimeLimit: true,
  });
  const [timeLimit] = useState("60");

  // Flag to disable all interactive features and show the login prompt
  const IS_DISABLED = true;

  // Placeholder functions to prevent errors on click
  const handleLogout = () => console.log("Logging out...");
  const handleSubmitTimeLimit = (e) => e.preventDefault();

  return (
    <div className="min-h-screen p-6 sm:p-12 bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 font-inter">
      <motion.div
        className="max-w-5xl mx-auto bg-white p-6 sm:p-12 rounded-3xl shadow-2xl border-t-6 border-purple-500"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >

        {/* === BACK BUTTON === */}
<button
  onClick={() => window.history.back()}
  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
>
  <ArrowLeft className="w-5 h-5" />
  <span className="font-medium">Back</span>
</button>

        {/* === SECTION 1: INFORMATIVE GUIDE HEADER === */}
        <header className="text-center mb-12">
          <div className="inline-block p-4 bg-purple-600 rounded-full shadow-lg mb-4">
            <Shield className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-4xl font-extrabold text-gray-800 tracking-tight leading-snug">
            Comprehensive Digital Safety Guide for Parents
          </h1>
          <p className="text-gray-500 mt-3 text-lg">
            A clear understanding of the purpose, timing, and mechanics of modern
            parental controls.
          </p>
        </header>

        {/* --- Informational Grid --- */}
        <motion.div
          className="mb-16 p-6 md:p-10 bg-purple-50 rounded-2xl shadow-inner border border-purple-200"
          variants={sectionVariants}
          transition={{ delay: 0.2 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {/* WHY */}
            <div className="border-l-4 border-purple-400 pl-4">
              <h3 className="font-bold text-purple-700 flex items-center gap-2 mb-3 text-xl">
                <Heart className="w-5 h-5" /> Why Controls are Essential
              </h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Controls are powerful tools for digital protection, ensuring
                children are shielded from mature content and helping to
                establish clear, healthy screen time limits.
              </p>
            </div>

            {/* WHEN */}
            <div className="border-l-4 border-pink-400 pl-4">
              <h3 className="font-bold text-pink-700 flex items-center gap-2 mb-3 text-xl">
                <Clock className="w-5 h-5" /> When to Start
              </h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Introduce controls proactively at an early age to set
                expectations. Settings should be adjusted dynamically as your
                child shows maturity and earns digital autonomy.
              </p>
            </div>

            {/* HOW */}
            <div className="border-l-4 border-yellow-400 pl-4">
              <h3 className="font-bold text-yellow-700 flex items-center gap-2 mb-3 text-xl">
                <Zap className="w-5 h-5" /> How it Works
              </h3>
              <p className="text-gray-600 text-base leading-relaxed">
                The system allows for remote management of daily screen time,
                uses advanced content filtering based on age, and provides
                detailed activity reports for oversight.
              </p>
            </div>
          </div>
        </motion.div>

        {/* === SECTION 2: DISABLED CONTROL PANEL SIMULATION === */}
        <header className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">
            Live Control Panel Simulation
          </h2>
          <p className="text-red-700 font-bold mt-4 p-3 border-2 border-red-500 bg-red-50 rounded-lg inline-block text-lg shadow-sm">
            🔒 Access Denied: Please authenticate to manage security settings.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Column 1: Profile Card */}
          <motion.div
            className="lg:col-span-1 bg-gray-50 p-8 rounded-2xl shadow-xl h-full border border-gray-200"
            variants={sectionVariants}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-5 border-b pb-3">
              <User className="w-6 h-6 text-purple-600" />
              Monitored Child Account
            </h2>

            <div className="space-y-4 text-base">
              <p>
                <strong>Name:</strong>{" "}
                <span className="text-gray-700">{childUser.name}</span>
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <span className="text-gray-700">{childUser.email}</span>
              </p>
              <p className="flex items-center gap-2 text-green-600 font-semibold">
                <CheckCircle className="w-5 h-5" />
                Monitoring Status: Active
              </p>
            </div>

            <motion.button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 mt-8 px-4 py-3 bg-gray-400 rounded-xl font-semibold text-white text-base opacity-70 cursor-not-allowed"
              disabled
            >
              <LogOut className="w-5 h-5" />
              Parent Logout Session
            </motion.button>
          </motion.div>

          {/* Column 2 & 3: Control Settings (Simulated) */}
          <motion.div
            className="lg:col-span-2 bg-white p-0 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden"
            variants={sectionVariants}
            transition={{ delay: 0.5 }}
          >
            {/* Full Overlay for Disabling Clicks (Prominent CTA) */}
            <div className="absolute inset-0 bg-purple-900 bg-opacity-95 flex flex-col items-center justify-center p-8 rounded-2xl z-10 text-white backdrop-blur-sm">
              <Lock className="w-12 h-12 text-pink-300 mb-4" />
              <h3 className="text-3xl font-extrabold mb-2">Access Locked</h3>
              <p className="text-purple-200 mb-8 text-lg text-center max-w-sm">
                To protect child settings, administrative access requires a
                secure parent login.
              </p>
              {/* Call to action button Text Size: text-xl */}
              <a
                href=""
                className="px-10 py-4 bg-yellow-400 text-purple-900 font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-2xl text-xl uppercase tracking-wider"
              >
                Secure Parent Login
              </a>
            </div>

            {/* Control Panel Header */}
            <div className="p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-6 border-b pb-4">
                <Settings className="w-6 h-6 text-pink-600" />
                Security & Limits Configuration
              </h2>
            </div>

            {/* Toggle Switches */}
            <div className="space-y-0.5 border-t border-gray-100">
              <ToggleSwitch
                label="Restrict Content (Age-Appropriate Filtering)"
                isChecked={controls.restrictContent}
                isDisabled={IS_DISABLED}
              />
              <ToggleSwitch
                label="Enable Notifications (Activity Alerts)"
                isChecked={controls.enableNotifications}
                isDisabled={IS_DISABLED}
              />
              <ToggleSwitch
                label="Daily Time Limit Enforcement"
                isChecked={controls.dailyTimeLimit}
                isDisabled={IS_DISABLED}
              />
            </div>

            {/* Conditional Time Limit Input (Visual) */}
            <motion.div
              className={`p-6 sm:p-8 mt-0 bg-purple-50 border-t border-purple-200 ${
                IS_DISABLED ? "opacity-50" : ""
              }`}
              initial={{
                height: controls.dailyTimeLimit ? "auto" : 0,
                opacity: controls.dailyTimeLimit ? 1 : 0,
              }}
              animate={{
                height: controls.dailyTimeLimit ? "auto" : 0,
                opacity: controls.dailyTimeLimit ? 1 : 0,
              }}
              transition={{ duration: 0.3 }}
              style={{ overflow: "hidden" }}
            >
              <form onSubmit={handleSubmitTimeLimit}>
                <label className="block text-base font-semibold text-purple-700 mb-2">
                  Set Daily Usage Limit (Minutes)
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={timeLimit}
                    onChange={() => {}} // Disabled for simulation
                    min="1"
                    required
                    className="flex-grow px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-300 text-base cursor-not-allowed"
                    placeholder="e.g., 60"
                    disabled={IS_DISABLED}
                  />
                  <motion.button
                    type="submit"
                    className="px-6 py-2 bg-gray-400 rounded-lg font-semibold text-white transition-colors text-base cursor-not-allowed"
                    disabled={IS_DISABLED}
                  >
                    Apply Limit
                  </motion.button>
                </div>
                <p className="text-sm text-purple-500 mt-2">
                  Current enforced limit:{" "}
                  <span className="font-bold">{timeLimit} minutes</span> per day.
                </p>
              </form>
            </motion.div>
          </motion.div>
        </div>

        {/* === COMING SOON BLOCK — EMAIL COLLECTION === */}
        <motion.div
          className="max-w-3xl mx-auto mt-16 p-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-3xl shadow-xl"
          variants={sectionVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.15 }}
        >
          <h2 className="text-3xl font-extrabold text-center mb-3">
            🚀 Great News for Parents!
          </h2>
          <p className="text-center text-lg opacity-90 mb-6">
            Advanced Parental Controls are{" "}
            <span className="font-bold">coming soon.</span> Enter your email to
            get early access notifications!
          </p>

          {/* Email Form (Now a separate, properly hoisted component) */}
          <EmailNotifyForm />
        </motion.div>
      </motion.div>
    </div>
  );
}